import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useSetSquawkConnectionType } from "./useSetSquawkConnectionType";
import { useGetSquawkConnectionType } from "./useGetSquawkConnectionType";
import useAuth from "@/hooks/useAuth";

type ConnectionType = "web_rtc" | "v2" | "web_sockets" | "shoutcast";

interface ConnectionOption {
  value: ConnectionType;
  label: string;
  badge: string;
  badgeColor: string;
  description: string;
  detail: string;
  techName: string;
}

const CONNECTION_OPTIONS: ConnectionOption[] = [
  {
    value: "web_rtc",
    label: "Recommended",
    badge: "Best",
    badgeColor: "bg-green-500/20 text-green-400 ring-green-500/30",
    description: "Lowest latency, real-time audio.",
    detail: "Best for most users.",
    techName: "WebRTC",
  },
  {
    value: "v2",
    label: "Resilient",
    badge: "New",
    badgeColor: "bg-squawk-blue/20 text-squawk-blue ring-squawk-blue/30",
    description: "Streams through most firewalls and proxies.",
    detail: "Use if Recommended keeps dropping.",
    techName: "Squawk v2",
  },
  {
    value: "web_sockets",
    label: "Alternative",
    badge: "Good",
    badgeColor: "bg-blue-500/20 text-blue-400 ring-blue-500/30",
    description: "Reliable fallback connection.",
    detail: "Use if Recommended isn't working.",
    techName: "WebSocket",
  },
  {
    value: "shoutcast",
    label: "Compatible",
    badge: "OK",
    badgeColor: "bg-gray-500/20 text-gray-400 ring-gray-500/30",
    description: "Maximum compatibility.",
    detail: "Slightly higher latency.",
    techName: "Shoutcast",
  },
];

function SquawkConnectionTypeActions() {
  const { authenticated } = useAuth();
  const { data: connectionType = "web_rtc" } = useGetSquawkConnectionType();
  const { mutate: setConnectionType } = useSetSquawkConnectionType();

  const handleSelect = (value: ConnectionType) => {
    if (authenticated) setConnectionType(value);
  };

  return (
    <div className="space-y-3">
      {CONNECTION_OPTIONS.map((option) => {
        const isSelected = connectionType === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`w-full text-left rounded-lg border p-4 transition-all duration-150 ${
              isSelected
                ? "border-squawk-blue bg-squawk-blue/10 ring-2 ring-squawk-blue cursor-pointer"
                : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 cursor-pointer"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">
                  {option.label}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${option.badgeColor}`}
                >
                  {option.badge}
                </span>
              </div>
              {isSelected && (
                <CheckCircleIcon className="size-5 text-squawk-blue shrink-0" />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-400">{option.description}</p>
            <p className="text-sm text-gray-500">{option.detail}</p>
            <p className="mt-1 text-xs text-gray-600">{option.techName}</p>
          </button>
        );
      })}
    </div>
  );
}

export default SquawkConnectionTypeActions;
